import { Inject } from "typescript-ioc";
import { UiComponentFactory } from "Ui/Scripts/UiComponentFactory";
import { EventBinder } from "Events/Scripts/EventBinder";
import { LoggerFactory } from "Logging/Scripts/LoggerFactory";

import * as DeliveryChannelsEventType from "./DeliveryChannelsEventType";
import { Utils } from "Helpers/Scripts/Utils";

import { IDeliveryComponent } from "./IDeliveryComponent";
import { IDeliveryChannelModel } from "./IDeliveryChannelModel";
import { ISelectedChannelAddress } from "./ISelectedChannelAddress";
import { IChannelUpdateEvent } from "./IChannelUpdateEvent";
import { DeliveryComponent } from "./DeliveryComponent";
import { IExtendedChannelsInfo } from "./IExtendedChannelsInfo";

export abstract class DeliveryChannel extends DeliveryComponent implements IDeliveryComponent {
    protected _model: IDeliveryChannelModel; // overwriting generic "object" type of _model property

    public abstract init(): void;
    public abstract update(nextState: IExtendedChannelsInfo, pageNumber?: number): void;

    private readonly DELIVERY_CHANNEL: string = "deliveryChannel";
    protected readonly KENDO_RADIO_BUTTON: string = "kendoRadioButton";
    protected readonly OUT_OF_STOCK_TRIGGER: string = "out-of-stock-trigger";
    protected readonly CHANNEL_TYPE_DATA_KEY: string = "channelType";
    protected readonly HOME_DELIVERY_CHANNEL: string = "HomeDeliveryChannel";
    protected readonly DATA_DELIVERY_CHANNEL: string = "data-delivery-channel";
    protected readonly DELIVERY_ADDRESS_PREFIX: string = "delivery-address-";
    protected readonly ADDRESS_LABEL_ID_PREFIX: string = "#label-" + this.DELIVERY_ADDRESS_PREFIX;
    protected readonly DATA_ADDRESS: string = "data-address";
    protected readonly DISABLED_ATTRIBUTE: string = "disabled";
    private readonly DISABLED_KENDO_CLASS: string = "k-state-disabled";
    private readonly SHOPPING_BAG_WRAPPER_SELECTOR: string = ".js-w-shopping-bag";
    public index: number = 0;

    constructor(
        @Inject componentFactory: UiComponentFactory,
        @Inject binder: EventBinder,
        @Inject loggerFactory: LoggerFactory
    ) {
        super(componentFactory, binder, loggerFactory);
    }

    /**
     * @returns The component model if provided
     */
    public get model(): IDeliveryChannelModel {
        return this._model;
    }

    public get key(): string {
        return "DeliveryChannel";
    }

    public toJSON(): object {
        return {
            index: this.index,
            key: this.key,
        };
    }

    protected _updateAddressInfo(data: IDeliveryChannelModel): void {
        const addressInfo = this.findElement(
            `[${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}="${data.DeliveryChannelId}"] .${this.DELIVERY_ADDRESS_INFO_CLASS}`
        );

        if (addressInfo) {
            Utils.setHtml(addressInfo, this._deliveryAddressInfoTemplate(data));
        }
    }

    private _updateChannelInfo(data: IDeliveryChannelModel): void {
        const channelInfo = this.findElement(
            `[${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}="${data.DeliveryChannelId}"] .${this.DELIVERY_CHANNEL_INFO_CLASS}`
        );
        if (channelInfo) {
            Utils.setHtml(channelInfo, this._deliveryChannelInfoTemplate(data));
            this._handleOutOfStockTooltip(data.OutOfStockItems, data.DeliveryChannelId);
            this._handleUnsupportedAddressTooltip(data.DeliveryChannelId);
        }
    }

    protected _showPhoneBox(deliveryChannel: number | null): void {
        if (!deliveryChannel) {
            return;
        }
        const selectedDeliveryChannelSection = this._getDeliveryChannelSection(deliveryChannel);
        if (selectedDeliveryChannelSection) {
            const phoneBoxToShow = Utils.find(
                selectedDeliveryChannelSection,
                this.DELIVERY_NOTIFICATIONS_PHONES_SELECTOR
            );
            if (phoneBoxToShow.length) {
                Utils.removeClass(phoneBoxToShow[0], this.CLASS_HIDDEN);
            }
        }
    }

    protected _isClickOnChannel(event: IChannelUpdateEvent): boolean {
        return (event.target as HTMLInputElement).name === this.DELIVERY_CHANNEL;
    }

    protected _triggerChannelUpdate(data: ISelectedChannelAddress): void {
        this._binder.trigger(DeliveryChannelsEventType.CHANNEL_UPDATE, data);
    }

    protected _makeChannelSelected(): void {
        const input = this.findElement("input:radio");
        if (input && !input.hasAttribute(this.DISABLED_ATTRIBUTE)) {
            Utils.getData(input, this.KENDO_RADIO_BUTTON).value(true);
        }
    }

    /**
     * Delivery channel can be disabled for a customer (Smartbox: ordered products doesn't match the box)
     */
    protected _toggleChannelSelectability(data: IDeliveryChannelModel): void {
        const input = this.findElement("input:radio") as Element;
        if (input) {
            const radioButton = kendo.getRadioButton(input);
            if (radioButton) {
                radioButton.enable(data.IsAllowed);
            }
        }
    }

    protected _updateFromDeliveryModel(
        prevState: IDeliveryChannelModel,
        nextState: IDeliveryChannelModel,
        forceUpdate: boolean
    ): boolean {
        if (!forceUpdate && !this._shouldComponentUpdate(prevState, nextState)) {
            return false;
        }

        if (this._isChanged(prevState.IsAllowed, nextState.IsAllowed) || forceUpdate) {
            this._toggleChannelSelectability(nextState);
        }

        const isOutOfStockChanged = this._wasOutOfStockItemsChanged(
            prevState.OutOfStockItems,
            nextState.OutOfStockItems
        );
        const isDeliveryDateChanged = this._isChanged(
            prevState.DeliveryDate,
            nextState.DeliveryDate
        );
        const isEndCustomerAddressChanged =
            this._isEndCustomerFirstCheckout() &&
            this._isChanged(prevState.Address, nextState.Address);

        if (isOutOfStockChanged || isDeliveryDateChanged || isEndCustomerAddressChanged) {
            this._updateChannelInfo(nextState);
        }

        if (this._isChanged(prevState.DeliveryFee, nextState.DeliveryFee)) {
            this._updateDeliveryFee(nextState.DeliveryFee, nextState.DeliveryChannelId);
        }

        if (this._isChanged(prevState.SelectedDeliveryChannel, nextState.SelectedDeliveryChannel)) {
            this._hidePhoneBoxes(prevState.SelectedDeliveryChannel);
        }

        return true;
    }

    protected _initRadioButton(selector: string | Element): kendo.ui.RadioButton | null {
        const radioButton = kendo.createRadioButton(selector);
        if (radioButton) {
            radioButton.element.addClass("invisible");
            return radioButton;
        }
        return null;
    }

    protected _getCurrentDeliveryChannelId(): number {
        if (ori.shopping && ori.shopping.wizard) {
            const shoppingContext = ori.shopping.wizard.getShoppingContext();
            return shoppingContext.DeliveryChannelId;
        }
        return 0;
    }

    protected _toggleCheckoutButtonEnabled(isEnabled: boolean): void {
        const checkoutButton = this._getCheckoutButton();
        if (checkoutButton) {
            Utils.toggleClass(checkoutButton, this.DISABLED_KENDO_CLASS, !isEnabled);
        }
    }

    private _getCheckoutButton(): Element | undefined {
        let element: Element | undefined = undefined;

        if (ori.shopping && ori.shopping.orderSummary) {
            const summaryBoxContext = Utils.parents(
                this.context,
                this.SHOPPING_BAG_WRAPPER_SELECTOR
            );
            if (summaryBoxContext.length) {
                element = ori.shopping.orderSummary.getCheckoutButtonInSummaryBox(
                    summaryBoxContext[0]
                )[0];
            }
        }

        return element;
    }
}
